import { useState } from "react";
import { Users, Minus, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function Row({ label, sub, value, set, min = 0, max = 12 }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="font-serif text-lg text-jade">{label}</p>
        <p className="text-[12px] text-ink/55">{sub}</p>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => set(Math.max(min, value - 1))}
          disabled={value <= min}
          className="w-8 h-8 border border-mist flex items-center justify-center text-ink/70 hover:border-jade disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label={`Decrease ${label}`}
          data-testid={`traveller-${label.toLowerCase().split(" ")[0]}-minus`}
        ><Minus className="w-3.5 h-3.5" strokeWidth={1.2} /></button>
        <span data-testid={`traveller-${label.toLowerCase().split(" ")[0]}-count`} className="font-serif text-xl text-jade w-6 text-center">{value}</span>
        <button
          type="button"
          onClick={() => set(Math.min(max, value + 1))}
          disabled={value >= max}
          className="w-8 h-8 border border-mist flex items-center justify-center text-ink/70 hover:border-jade disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label={`Increase ${label}`}
          data-testid={`traveller-${label.toLowerCase().split(" ")[0]}-plus`}
        ><Plus className="w-3.5 h-3.5" strokeWidth={1.2} /></button>
      </div>
    </div>
  );
}

export default function TravellersPicker({ value, onChange }) {
  const { adults, children, infants } = value;
  const [open, setOpen] = useState(false);
  const set = (k) => (v) => onChange({ ...value, [k]: v });

  const summary = () => {
    const parts = [`${adults} adult${adults === 1 ? "" : "s"}`];
    if (children > 0) parts.push(`${children} child${children === 1 ? "" : "ren"}`);
    if (infants > 0) parts.push(`${infants} infant${infants === 1 ? "" : "s"}`);
    return parts.join(", ");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="travellers-trigger"
          className="w-full text-left input-line flex items-center justify-between cursor-pointer"
        >
          <span className="flex items-center gap-3 text-ink/85">
            <Users className="w-4 h-4 text-ink/55" strokeWidth={1.2} />
            {summary()}
          </span>
          <span className="text-[11px] tracking-[0.16em] uppercase text-jade">Change</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] bg-ivory border-mist p-6 rounded-none shadow-lg">
        <p className="eyebrow mb-2">Travellers</p>
        <Row label="Adults" sub="18 and over" value={adults} set={set("adults")} min={1} max={12} />
        <div className="h-px bg-mist/60" />
        <Row label="Children" sub="Ages 2 to 17" value={children} set={set("children")} min={0} max={8} />
        <div className="h-px bg-mist/60" />
        <Row label="Infants" sub="Ages 0 to 1" value={infants} set={set("infants")} min={0} max={4} />
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="btn-primary w-full mt-5 text-[11px]"
        >Done</button>
      </PopoverContent>
    </Popover>
  );
}
